import { Subscription } from '@rails/actioncable';
import { BulkOperation_Read, BulkOperationStatus } from '@treadinc/horizon-api-spec';
import { uniqBy } from 'lodash-es';

import connection from '~services/connectionModule';

const bulkOperationStatusRank = (status: BulkOperationStatus) => {
  if (status === BulkOperationStatus.STARTED) {
    return 0;
  }

  if (status === BulkOperationStatus.IN_PROGRESS) {
    return 1;
  }

  return 2;
};

export const isBulkOperationSettled = (status: BulkOperationStatus) =>
  status === BulkOperationStatus.FINISHED || status === BulkOperationStatus.ERROR;

// The channel transmits `{ error }` before rejecting a subscription, which reaches `received`
// like a real message.
const isBulkOperationMessage = (message: unknown): message is BulkOperation_Read =>
  Boolean(message) && typeof message === 'object' && 'status' in (message as object);

export function initialBulkOperationProgress(id: string, count: number) {
  const progress: BulkOperation_Read = {
    count,
    failed: [],
    failed_count: 0,
    id,
    status: BulkOperationStatus.STARTED,
    succeeded: [],
    succeeded_count: 0,
  };

  return progress;
}

// Progress from parallel workers can arrive out of order, so counts, items, and status only
// ever move forward.
export function mergeBulkOperationProgress<T extends BulkOperation_Read>(
  previous: T | undefined,
  next: T,
) {
  if (!previous) {
    return next;
  }

  const nextStatusIsBehind =
    bulkOperationStatusRank(next.status) < bulkOperationStatusRank(previous.status);
  const previousIsSettled = isBulkOperationSettled(previous.status);

  return {
    ...next,
    count: Math.max(previous.count, next.count),
    failed: uniqBy([...previous.failed, ...next.failed], 'id'),
    failed_count: Math.max(previous.failed_count, next.failed_count),
    status: previousIsSettled || nextStatusIsBehind ? previous.status : next.status,
    succeeded: uniqBy([...previous.succeeded, ...next.succeeded], 'id'),
    succeeded_count: Math.max(previous.succeeded_count, next.succeeded_count),
  };
}

export function subscribeToBulkOperation<T extends BulkOperation_Read>(
  channel: string,
  id: string,
  handlers: { received: (message: T) => void },
): Subscription | undefined {
  const consumer = connection.realTimeConnection;
  const existingSubscription = consumer?.subscriptions.subscriptions.find(
    (subscription) =>
      subscription.identifier.includes(channel) && subscription.identifier.includes(id),
  );

  if (existingSubscription) {
    return existingSubscription;
  }

  return consumer?.subscriptions?.create?.(
    { channel, id },
    {
      received(message: unknown) {
        if (isBulkOperationMessage(message)) {
          handlers.received(message as T);
        }
      },
    },
  );
}
